import { useState } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  Card,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Badge,
  Box,
  Divider,
} from "@mantine/core";
import {
  IconTrash,
  IconPlus,
  IconMessage,
  IconPencil,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useAuth } from "../../hooks/useAuth";
import {
  useCaseComments,
  useAddComment,
  useUpdateComment,
  useDeleteComment,
} from "../../hooks/useInternalDiscussions";
import type { CaseComment } from "../../services/internalDiscussions.service";
import RichTextDiagnosis from "./RichTextDiagnosis";

interface CommentsCenterProps {
  caseId: string | null;
}

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export function CommentsCenter({ caseId }: CommentsCenterProps) {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");

  const { data: allComments = [], isLoading } = useCaseComments(caseId || "");
  const comments = allComments.filter(
    (comment) => comment.internalDiscussionId === null
  );

  const addCommentMutation = useAddComment(
    user ? { id: user.id, fullName: user.fullName } : undefined
  );
  const updateCommentMutation = useUpdateComment();
  const deleteCommentMutation = useDeleteComment();

  const isContentEmpty = (html: string) => !html.replace(/<[^>]*>/g, "").trim();

  const handleAddComment = () => {
    if (!isContentEmpty(newComment) && caseId) {
      addCommentMutation.mutate(
        {
          caseId,
          content: newComment,
          isInternal: true,
        },
        {
          onSuccess: () => {
            setNewComment("");
            setIsAdding(false);
            notifications.show({
              title: "Comment Added",
              message: "Case comment has been saved",
              color: "green",
            });
          },
          onError: (error: any) => {
            notifications.show({
              title: "Error",
              message: error.message || "Failed to add comment",
              color: "red",
            });
          },
        }
      );
    }
  };

  const handleEditComment = (comment: CaseComment) => {
    setEditingCommentId(comment.id);
    setEditingContent(comment.content);
  };

  const handleSaveEdit = () => {
    if (editingCommentId && !isContentEmpty(editingContent)) {
      updateCommentMutation.mutate(
        {
          commentId: editingCommentId,
          data: { content: editingContent },
        },
        {
          onSuccess: () => {
            setEditingCommentId(null);
            setEditingContent("");
            notifications.show({
              title: "Comment Updated",
              message: "Case comment has been updated",
              color: "green",
            });
          },
          onError: (error: any) => {
            notifications.show({
              title: "Error",
              message: error.message || "Failed to update comment",
              color: "red",
            });
          },
        }
      );
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingContent("");
  };

  const handleDeleteComment = (commentId: string) => {
    deleteCommentMutation.mutate(commentId, {
      onSuccess: () => {
        notifications.show({
          title: "Comment Deleted",
          message: "Case comment has been removed",
          color: "blue",
        });
      },
      onError: (error: any) => {
        notifications.show({
          title: "Error",
          message: error.message || "Failed to delete comment",
          color: "red",
        });
      },
    });
  };

  const isEdited = (comment: CaseComment) => {
    const created = new Date(comment.createdAt).getTime();
    const updated = new Date(comment.updatedAt).getTime();
    return updated - created > 1000;
  };

  const canModify = (comment: CaseComment) => comment.userId === user?.id;

  if (!caseId) {
    return (
      <Box>
        <Text c="dimmed" ta="center" size="sm" py="xl">
          Save the case first to add comments
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <IconMessage size={20} color="var(--mantine-color-teal-6)" />
          <Text size="lg" fw={600}>
            Comments
          </Text>
          {comments.length > 0 && (
            <Badge size="sm" variant="light" color="blue">
              {comments.length}
            </Badge>
          )}
        </Group>
        {!isAdding && (
          <Button
            size="sm"
            color="blue"
            leftSection={<IconPlus size={14} />}
            onClick={() => setIsAdding(true)}
          >
            Add Comment
          </Button>
        )}
      </Group>

      <Divider />

      {/* Add comment form */}
      {isAdding && (
        <Stack gap="sm">
          <RichTextDiagnosis
            value={newComment}
            onChange={setNewComment}
            placeholder="Enter comment..."
            minHeight={120}
          />
          <Group justify="flex-end" gap="xs">
            <Button
              size="sm"
              variant="subtle"
              onClick={() => {
                setIsAdding(false);
                setNewComment("");
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              color="blue"
              onClick={handleAddComment}
              loading={addCommentMutation.isPending}
              disabled={isContentEmpty(newComment)}
            >
              Save Comment
            </Button>
          </Group>
        </Stack>
      )}

      {/* Comments list */}
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : comments.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconMessage size={40} color="var(--mantine-color-gray-4)" />
            <Text c="dimmed" size="sm">
              No comments yet. Add the first one.
            </Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="md">
          {comments.map((comment: CaseComment) => (
            <Card key={comment.id} withBorder p="md" shadow="xs">
              {editingCommentId === comment.id ? (
                <Stack gap="sm">
                  <Text size="sm" fw={500}>
                    Edit Comment
                  </Text>
                  <RichTextDiagnosis
                    value={editingContent}
                    onChange={setEditingContent}
                    minHeight={100}
                  />
                  <Group justify="flex-end" gap="xs">
                    <Button
                      size="sm"
                      variant="subtle"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      color="blue"
                      onClick={handleSaveEdit}
                      loading={updateCommentMutation.isPending}
                      disabled={isContentEmpty(editingContent)}
                    >
                      Save
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{ wordBreak: "break-word" }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: comment.content }}
                    />
                    <Text size="xs" c="dimmed">
                      {comment.user?.fullName || "Unknown"} •{" "}
                      {formatDate(comment.createdAt)}
                      {isEdited(comment) && (
                        <Text span c="dimmed" fs="italic">
                          {" "}
                          (edited)
                        </Text>
                      )}
                    </Text>
                  </Stack>
                  {canModify(comment) && (
                    <Group gap={4}>
                      <Tooltip label="Edit comment">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={() => handleEditComment(comment)}
                        >
                          <IconPencil size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete comment">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => handleDeleteComment(comment.id)}
                          loading={deleteCommentMutation.isPending}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )}
                </Group>
              )}
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
